import type { PaigeClient } from '@paige/api-client';
import { unwrap, type Problem } from './http';
import { ChannelRefusal, type PartnerAccountsApi } from './partner-ledger-api';

const z = (zohoAccountId: string) => ({ path: { zohoAccountId } });
const page = (cursor: string | null) => (cursor ? { cursor } : {});

/** The partner account page over the admin API (ADR-0036, section 7.1). */
export function partnerAccountsApi(client: PaigeClient): PartnerAccountsApi {
  return {
    ...accountReads(client),
    ...listReads(client),
    ...standing(client),
    ...statementWrites(client),
  };
}

function accountReads(
  client: PaigeClient,
): Pick<PartnerAccountsApi, 'find' | 'account' | 'channel'> {
  return {
    async find(q, cursor) {
      const query = { ...(q ? { q } : {}), ...page(cursor) };
      return unwrap(await client.GET('/admin/partner-accounts', { params: { query } }));
    },
    async account(zoho) {
      return unwrap(
        await client.GET('/admin/partner-accounts/{zohoAccountId}', { params: z(zoho) }),
      );
    },
    async channel(zoho) {
      const params = z(zoho);
      return unwrap(
        await client.GET('/admin/partner-accounts/{zohoAccountId}/invoice-channel', { params }),
      );
    },
  };
}

function listReads(
  client: PaigeClient,
): Pick<PartnerAccountsApi, 'companies' | 'statements' | 'statement'> {
  return {
    async companies(zoho, cursor) {
      const params = { ...z(zoho), query: page(cursor) };
      return unwrap(
        await client.GET('/admin/partner-accounts/{zohoAccountId}/companies', { params }),
      );
    },
    async statements(zoho, cursor) {
      const params = { ...z(zoho), query: page(cursor) };
      return unwrap(
        await client.GET('/admin/partner-accounts/{zohoAccountId}/statements', { params }),
      );
    },
    async statement(zoho, statementId) {
      const params = { path: { zohoAccountId: zoho, statementId } };
      const path = '/admin/partner-accounts/{zohoAccountId}/statements/{statementId}' as const;
      return unwrap(await client.GET(path, { params }));
    },
  };
}

function standing(
  client: PaigeClient,
): Pick<PartnerAccountsApi, 'setChannel' | 'suspend' | 'resume'> {
  const suspension = '/admin/partner-accounts/{zohoAccountId}/suspension' as const;
  return {
    async setChannel(zoho, body, key) {
      const params = { ...z(zoho), header: { 'Idempotency-Key': key } };
      const r = await client.PUT('/admin/partner-accounts/{zohoAccountId}/invoice-channel', {
        params,
        body,
      });
      // A switch refused for companies billed the other way names them; the dialog lists them.
      if (r.response.status === 409 && typeof r.error === 'object' && r.error)
        throw new ChannelRefusal(409, r.error as Problem);
      return unwrap(r);
    },
    async suspend(zoho, body) {
      return unwrap(await client.PUT(suspension, { params: z(zoho), body }));
    },
    async resume(zoho, reason) {
      return unwrap(await client.DELETE(suspension, { params: { ...z(zoho), query: { reason } } }));
    },
  };
}

function statementWrites(
  client: PaigeClient,
): Pick<PartnerAccountsApi, 'adjust' | 'markPaid' | 'voidAndReissue'> {
  const n = (number: string) => ({ path: { number } });
  return {
    async adjust(zoho, body, key) {
      return unwrap(
        await client.POST('/admin/partner-accounts/{zohoAccountId}/adjustments', {
          params: { ...z(zoho), header: { 'Idempotency-Key': key } }, // the line's key, one per dialog
          body,
        }),
      );
    },
    async markPaid(number, body) {
      return unwrap(
        await client.POST('/admin/partner-statements/{number}/paid', { params: n(number), body }),
      );
    },
    async voidAndReissue(number, body) {
      return unwrap(
        await client.POST('/admin/partner-statements/{number}/void', { params: n(number), body }),
      );
    },
  };
}
